import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const RESULT_INFO = {
  success: { l: "Başarılı giriş", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { l: "Hatalı şifre", c: "bg-amber-50 text-amber-700 border-amber-200" },
  locked: { l: "Hesap kilitlendi", c: "bg-rose-50 text-rose-700 border-rose-200" },
  blocked: { l: "Kilitliyken denendi", c: "bg-rose-50 text-rose-700 border-rose-200" },
  unlocked: { l: "Kilit açıldı", c: "bg-blue-50 text-blue-700 border-blue-200" },
};
const FILTERS = [{ k: "", l: "Tümü" }, ...Object.entries(RESULT_INFO).map(([k, v]) => ({ k, l: v.l }))];

export const LoginEventsCard = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState("");

  useEffect(() => {
    api.get("/login-events", { params: { page, page_size: 25, ...(result ? { result } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, page_size: 25 }));
  }, [page, result, refreshKey]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <Card className="border border-slate-200 shadow-sm mt-8 overflow-hidden" data-testid="login-events-card">
      <div className="p-5 pb-3">
        <h3 className="font-display font-semibold text-slate-900">Giriş Geçmişi</h3>
        <p className="text-xs text-slate-500 mt-1">Son 90 günün girişleri, hatalı denemeleri ve kilitlenmeleri.</p>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="login-events-filters">
          {FILTERS.map((f) => (
            <button key={f.k || "all"} type="button" onClick={() => { setPage(1); setResult(f.k); }}
                    data-testid={`login-filter-${f.k || "all"}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${result === f.k
                      ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-600"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="login-events-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Tarih / Saat</TableHead>
              <TableHead>Kullanıcı Adı</TableHead>
              <TableHead>Kişi</TableHead>
              <TableHead>Sonuç</TableHead>
              <TableHead>IP Adresi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500" data-testid="login-events-empty">Kayıt yok.</TableCell></TableRow>
            )}
            {data?.items.map((e) => (
              <TableRow key={e.id} data-testid={`login-event-${e.id}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{formatDateTime(e.at)}</TableCell>
                <TableCell className="font-mono text-xs">{e.username}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{e.full_name || <span className="text-slate-400">Bilinmeyen kullanıcı</span>}</TableCell>
                <TableCell>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border whitespace-nowrap ${RESULT_INFO[e.result]?.c || ""}`}>
                    {RESULT_INFO[e.result]?.l || e.result}{e.by ? ` · ${e.by}` : ""}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{e.ip || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 p-3 text-sm text-slate-600">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="login-events-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span data-testid="login-events-page">{page} / {pages}</span>
          <Button variant="outline" size="icon" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="login-events-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </Card>
  );
};
